/// <amd-module name="Environments/EnvironmentDetails/Stores/EnvironmentDeploymentsHistoryStore" />
import { TaskOrchestrationOwner, TaskResult } from "DistributedTask/Client/Contracts/TaskAgent";
import { JobDetails } from "Environments/EnvironmentDetails/Resources";
import { IEnvironmentDeploymentHistoryResult, IEnvironmentDeploymentHistoryViewRecord, IEnvironmentDeploymentJobRecord, IEnvironmentDeploymentJobStatus } from "Environments/Client/EnvironmentContracts";
import { EnvironmentDeploymentsHistoryActions } from "Environments/EnvironmentDetails/Actions/EnvironmentDeploymentsHistoryActions";
import { defaultComparer as DateComparer } from "Environments/EnvironmentsCommon/DateUtils"
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { StoreBase, IStoreState } from "Environments/Flux/Store"

import { localeFormat } from "VSS/Core/Util/String";

export const LinkedPipelinesUpdatedEvent = "Linked_Pipelines_Definitions_Updated";

export interface IEnvironmentDeploymentRecord {
    definitionId: number,
    definitionName: string,
    definitionUrl: string,
    ownerId: number,
    ownerName: string,
    ownerUrl: string,
    ownerSourceInfoMessage: string,
    queueTime: Date | undefined,
    finishTime: Date | undefined,
    jobsInfo: string,
    jobsLink: string,
    status?: IEnvironmentDeploymentJobStatus,
    planType: string;
    maxJobRecordId: number;
}

export interface IEnvironmentDeploymentsHistoryStoreState extends IStoreState {
    records: IEnvironmentDeploymentRecord[],
    continuationToken?: number;
    pipelineDefinitions: TaskOrchestrationOwner[];
}

export class EnvironmentDeploymentsHistoryStore extends StoreBase {
    
    public static getKey(): string {
        return "environment-deployments-history-store";
    }
    
    constructor(historyResult: IEnvironmentDeploymentHistoryResult) {
        super("EnvironmentDeploymentsHistoryChangedEvent");
        if (!historyResult) {
            this._state.records = [];
            this._state.continuationToken = undefined;
        } else {
            this._state.records = this.mapExecutionRecords(historyResult.deploymentExecutionRecords);
            this._state.continuationToken = historyResult.continuationToken;    
        }      
    }

    public initialize(instanceId: string): void {
        super.initialize(instanceId);
        
        this._actionsHub = ActionsHubManager.GetActionsHub<EnvironmentDeploymentsHistoryActions>(EnvironmentDeploymentsHistoryActions, instanceId);
        this._actionsHub.updateExecutionRecords.addListener(this._updateExecutionRecords);
        this._actionsHub.appendExecutionRecords.addListener(this._appendExecutionRecords);
        this._actionsHub.updatePipelineDefinitions.addListener(this._updateLinkedPipelineDefinitions);
    }
    
    protected disposeInternal(): void {
        this._actionsHub.updateExecutionRecords.removeListener(this._updateExecutionRecords);
        this._actionsHub.appendExecutionRecords.removeListener(this._appendExecutionRecords);
        this._actionsHub.updatePipelineDefinitions.removeListener(this._updateLinkedPipelineDefinitions);
    }

    public getState(): IEnvironmentDeploymentsHistoryStoreState {
        return this._state;
    }

    public getDeploymentRecords(): IEnvironmentDeploymentRecord[] {
        return !!this._state.records ? this._state.records : [];
    }

    public getLinkedPipelineDefinitions(): TaskOrchestrationOwner[] {
        return !!this._state.pipelineDefinitions ? this._state.pipelineDefinitions : [];
    }

    public getContinuationToken(): number | undefined {
        return !!this._state.continuationToken ? this._state.continuationToken : undefined;
    }

    private _updateExecutionRecords = (historyResult: IEnvironmentDeploymentHistoryResult) => {
        if (!historyResult) {
            this._state.records = [];
            this._state.continuationToken = undefined;
        } else {
            this._state.records = this.mapExecutionRecords(historyResult.deploymentExecutionRecords);
            this._state.continuationToken = historyResult.continuationToken;    
        }
        
        this.emitChanged();
    }

    private _appendExecutionRecords = (historyResult: IEnvironmentDeploymentHistoryResult) => {
        if (!historyResult) {
            return;
        }

        const records: IEnvironmentDeploymentRecord[] = this.mapExecutionRecords(historyResult.deploymentExecutionRecords);
        records.forEach((record) => {
            this._state.records.push(record);
        });

        this._state.continuationToken = historyResult.continuationToken;
      
        this.emitChanged();
    }

    private _updateLinkedPipelineDefinitions = (pipelineDefinitions: TaskOrchestrationOwner[]): void => {
        this._state.pipelineDefinitions = pipelineDefinitions;
        this.emit(LinkedPipelinesUpdatedEvent, this);
    }

    public mapExecutionRecords(records: IEnvironmentDeploymentHistoryViewRecord[]) {
        if (!records) {
            return [];
        }

        var historyRecords = records.map((record: IEnvironmentDeploymentHistoryViewRecord): IEnvironmentDeploymentRecord => {
            return {
                definitionId: this._getIdFromReference(record.definition),
                definitionName: this._getNameFromReference(record.definition),
                definitionUrl: this._getWebUrlFromReference(record.definition),
                ownerId: this._getIdFromReference(record.owner),
                ownerName: this._getNameFromReference(record.owner),
                ownerSourceInfoMessage: record.ownerSourceInfoMessage,
                ownerUrl: this._getWebUrlFromReference(record.owner),
                queueTime: this._getQueueTime(record.jobs),
                finishTime: this._getFinishTime(record.jobs),
                jobsInfo: this._getJobsInfo(record.jobs),
                jobsLink: "",
                status: this._getJobStatus(record.jobs),
                planType: record.planType,
                maxJobRecordId: this._getJobsMaxRecordId(record.jobs)
            }
        });

        return historyRecords.sort((a, b) => { return DateComparer(b.queueTime, a.queueTime); });
    }

    private _getJobsMaxRecordId(jobs: IEnvironmentDeploymentJobRecord[]): number { 
        let maxJobRecordId = 0;
        if (!!jobs && jobs.length > 0) {
            jobs.forEach((job: IEnvironmentDeploymentJobRecord) => {
                if (job.environmentExecutionRecordId > maxJobRecordId) {
                    maxJobRecordId = job.environmentExecutionRecordId;
                }
            });
        }

        return maxJobRecordId;
    }

    private _getQueueTime(jobs: IEnvironmentDeploymentJobRecord[]): Date | undefined {
        if (!jobs || jobs.length === 0) {
            return undefined;
        }

        let minQueueTime: Date | undefined = undefined;
        jobs.forEach((job: IEnvironmentDeploymentJobRecord) => {
            if (!minQueueTime) {
                minQueueTime = job.queueTime;
                
                return;
            }

            if (!!job.queueTime && DateComparer(job.queueTime, minQueueTime) < 0) {
                minQueueTime = job.queueTime;
            }
        });

        return minQueueTime;
    }

    private _getFinishTime(jobs: IEnvironmentDeploymentJobRecord[]): Date | undefined {
        if (!jobs || jobs.length === 0) {
            return undefined;
        }

        // If any job is in-complete
        if (!!jobs.find((j) => { return !j.finishTime; })) {
            return undefined;
        }

        let maxFinishTime: Date = jobs[0].finishTime;
        jobs.forEach((job: IEnvironmentDeploymentJobRecord) => {
            if (DateComparer(job.finishTime, maxFinishTime) > 0) {
                maxFinishTime = job.finishTime;
            }
        });

        return maxFinishTime;
    }

    private _getJobsInfo(jobs: IEnvironmentDeploymentJobRecord[]): string {
        if (!jobs || jobs.length === 0) {
            return "";
        }

        if (jobs.length === 1) {
            return jobs[0].jobName;
        }

        jobs = jobs.sort((a, b) => { return DateComparer(a.startTime, b.startTime); });

        return localeFormat(JobDetails, jobs[0].jobName, jobs.length - 1);
    }

    private _mergeTaskResult(result: TaskResult, childResult: TaskResult) {
        // Do not change the check to "!result".
        // Explicit null and undefined checks are needed
        // to avoid not returning when result == TaskResult.Succeded 
        if (result === null || result === undefined) {
            return childResult;
        }

        if (result === childResult) {
            return result;
        }
        
        if (result === TaskResult.Failed
            || result === TaskResult.Abandoned
            || result === TaskResult.Skipped) {
                return TaskResult.Failed;
        }

        switch(childResult) {
            case TaskResult.Canceled:
                return TaskResult.Canceled;
            case TaskResult.Failed:
            case TaskResult.Abandoned:
            case TaskResult.Skipped:
                result = TaskResult.Failed;
                break;
            case TaskResult.SucceededWithIssues:
                if (result === TaskResult.Succeeded)    
                {
                    result = TaskResult.SucceededWithIssues;
                }            
        }

        return result;
    }

    private _isAnyJobInprogress(jobs: IEnvironmentDeploymentJobRecord[]): boolean {
        // A job with NON-NULL StartTime and NULL FinishTime is considered to be in-progress.
        return !!jobs.find((j) => { return !!j.startTime && !j.finishTime });
    }

    private _hasAnyJobNotStarted(jobs: IEnvironmentDeploymentJobRecord[]): boolean {
        // A job with NULL StartTime and NULL FinishTime is considered to be not started.
        return !!jobs.find((j) => { return !j.startTime });
    }

    private _getJobStatus(jobs: IEnvironmentDeploymentJobRecord[]): IEnvironmentDeploymentJobStatus | undefined {
        if (!jobs || jobs.length === 0) {
            return undefined;
        }

        const jobInprogress = this._isAnyJobInprogress(jobs);
        const jobNotStarted = this._hasAnyJobNotStarted(jobs);
        let result: TaskResult = jobs[0].result;

        for(let index = 1; index < jobs.length; index++) {
            if (!!jobs[index].result) {
                result = this._mergeTaskResult(result, jobs[index].result);
            }
        }

        let jobStatus: any = result;
        if (result === TaskResult.Succeeded || result === TaskResult.SucceededWithIssues) {            
            if (jobInprogress || jobNotStarted) {
                jobStatus = IEnvironmentDeploymentJobStatus.Inprogress;
            }
        }

        if (result === undefined || result === null) {
            if (jobNotStarted) {
                jobStatus = IEnvironmentDeploymentJobStatus.NotStarted;
            }

            if (jobInprogress) {
                jobStatus = IEnvironmentDeploymentJobStatus.Inprogress;
            }
        }

        return jobStatus;
    }

    private _getIdFromReference(reference: TaskOrchestrationOwner): number {
        return !!reference ? reference.id : 0;
    }

    private _getNameFromReference(reference: TaskOrchestrationOwner): string {
        return !!reference ? reference.name : "";
    }

    private _getWebUrlFromReference(reference: TaskOrchestrationOwner): string {
        if (!!reference
            && !!reference._links
            && !!reference._links.web
            && !!reference._links.web.href) {
                return reference._links.web.href;
            }

        return "";
    }
    
    private _actionsHub: EnvironmentDeploymentsHistoryActions;
    private _state: IEnvironmentDeploymentsHistoryStoreState = {} as IEnvironmentDeploymentsHistoryStoreState;
}
